import { Divider, Modal, Button, CircularProgress, Alert, Box } from "@mui/material";
import PlagiarismOutlinedIcon from "@mui/icons-material/PlagiarismOutlined";
import { useRecoilState } from "recoil";
import { shipmentDetailModalState } from "../../../atoms/myShipments";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import FmdGoodIcon from "@mui/icons-material/FmdGood";
import MessageTime from "../../../utils/MessageTime";
import IsoToLatLong from "country-iso-to-coordinates";
import { ComposableMap, Geographies, Geography, Graticule, Line, Marker } from "react-simple-maps";
import features from "../../../json/features.json";
import { useState, useEffect } from "react";
import { getTrackingUrl } from "../../../utils/TrackingLink";

const ShipmentDetail = () => {
  const [shipmentDetailModal, setShipmentDetailModal] = useRecoilState(shipmentDetailModalState);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Responsive styles
  const isMobile = window.innerWidth <= 768;
  const isTablet = window.innerWidth <= 1024;

  const handleShipmentDetailClose = () => {
    setShipmentDetailModal({ status: false, data: [], shipment: [] });
    setIsLoading(true);
    setHasError(false);
    setErrorMessage("");
  };

  // Veri doğrulama ve koordinat hesaplama
  const senderCountryCoordinates = shipmentDetailModal.shipment?.sender_country
    ? IsoToLatLong[shipmentDetailModal.shipment.sender_country]
    : null;
  const receiverCountryCoordinates = shipmentDetailModal.shipment?.receiver_country
    ? IsoToLatLong[shipmentDetailModal.shipment.receiver_country]
    : null;

  const trackingCargo = shipmentDetailModal?.shipment?.get_parcel_supplier?.supplier_name;

  // Modal açıldığında loading state'ini yönet
  useEffect(() => {
    if (shipmentDetailModal.status) {
      setIsLoading(true);
      setHasError(false);

      // Veri kontrolü
      if (!shipmentDetailModal.shipment || !shipmentDetailModal.data) {
        setHasError(true);
        setErrorMessage("Gönderi bilgileri yüklenemedi.");
        setIsLoading(false);
        return;
      }

      // Koordinat kontrolü
      if (!senderCountryCoordinates || !receiverCountryCoordinates) {
        console.warn("Ülke koordinatları bulunamadı:", {
          sender: shipmentDetailModal.shipment?.sender_country,
          receiver: shipmentDetailModal.shipment?.receiver_country
        });
      }

      // Kısa bir delay ile loading'i kapat (veri hazır olduğunu simüle et)
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [shipmentDetailModal.status, shipmentDetailModal.shipment, shipmentDetailModal.data, senderCountryCoordinates, receiverCountryCoordinates]);

  const cargoSites = [
    {
      name: "FEDEX",
      keyword: "FX",
      link: "https://www.fedex.com/fedextrack/?trknbr=",
    },
    {
      name: "HERMES",
      keyword: "",
      link: "https://www.myhermes.de/empfangen/sendungsverfolgung/?trackingId=",
    },
    {
      name: "FEDEX PAK",
      keyword: "FX",
      link: "https://www.fedex.com/fedextrack/?trknbr=",
    },
    {
      name: "UPS",
      keyword: "1Z",
      link: "https://www.ups.com/track?loc=tr_TR&tracknum=",
    },
    {
      name: "GLS",
      keyword: "500",
      link: "https://www.gls-pakete.de/en/parcel-tracking?trackingNumber=",
    },
    {
      name: "UPS DIRECT",
      keyword: "1Z1",
      link: "https://www.ups.com/track?loc=tr_TR&tracknum=",
    },
    {
      name: "UPS ECO",
      keyword: "1Z1",
      link: "https://www.ups.com/track?loc=tr_TR&tracknum=",
    },
    {
      name: "UPS EXPRESS",
      keyword: "1Z",
      link: "https://www.ups.com/track?loc=tr_TR&tracknum=",
    },
    {
      name: "UPS/Mhl",
      keyword: "1Z",
      link: "https://www.ups.com/track?loc=tr_TR&tracknum=",
    },
    {
      name: "UPS Saver",
      keyword: "1Z",
      link: "https://www.ups.com/track?loc=tr_TR&tracknum=",
    },
    {
      name: "DPD",
      keyword: "0150",
      link: "https://tracking.dpd.de/status/en_NL/parcel/",
    },
    {
      name: "ROYAL MAIL",
      keyword: "",
      link: "https://www.royalmail.com/track-your-item#/tracking-results/",
    },
    {
      name: "ROYAL MAIL-ORTA BOY KOLİ",
      keyword: "",
      link: "https://www.royalmail.com/track-your-item#/tracking-results/",
    },
    {
      name: "ROYAL MAIL-BÜYÜK BOY KOLİ",
      keyword: "",
      link: "https://www.royalmail.com/track-your-item#/tracking-results/",
    },
    {
      name: "ROYAL MAIL-KÜÇÜK BOY KOLİ",
      keyword: "",
      link: "https://www.royalmail.com/track-your-item#/tracking-results/",
    },
    {
      name: "ROYAL MAIL-ZARF",
      keyword: "",
      link: "https://www.royalmail.com/track-your-item#/tracking-results/",
    },
    {
      name: "DHL",
      keyword: "",
      link: "https://www.dhl.com/tr-tr/home/tracking.html?tracking-id=",
    },
    {
      name: "DHL ECO",
      keyword: "",
      link: "https://www.dhl.com/tr-tr/home/tracking/tracking-ecommerce.html?submit=1&tracking-id=",
    },
    {
      name: "DHL ECO - US",
      keyword: "",
      link: "https://www.dhl.com/tr-tr/home/tracking/tracking-ecommerce.html?submit=1&tracking-id=",
    },
    {
      name: "USPS",
      keyword: "9400",
      link: "https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=",
    },
    {
      name: "TNT",
      keyword: "9740",
      link: "https://www.tnt.com/express/tr_tr/site/shipping-tools/tracking.html?searchType=con&cons=",
    },
  ];

  const getTrackingLinkByCargo = (cargoName, trackingNumber) => {
    const selectedCargo = cargoSites.find((cargo) => cargo.name === cargoName);
    const trackingLink =
      getTrackingUrl(cargoName, trackingNumber) ||
      (selectedCargo ? selectedCargo.link + trackingNumber : null);

    if (trackingLink) {
      window.open(trackingLink, "_blank", "noopener,noreferrer");
    } else {
      alert("Belirtilen kargo firması için uygun bir takip linki bulunamadı.");
    }
  };

  return (
    <Modal
      open={shipmentDetailModal.status}
      onClose={handleShipmentDetailClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        overflow: 'auto'
      }}
    >
      <div
        style={{
          maxWidth: isMobile ? "95%" : isTablet ? "95%" : "1200px",
          width: "100%",
          backgroundColor: "var(--lighterBg)",
          borderRadius: "var(--inputBorderRadius)",
          margin: isMobile ? "10px auto" : "20px auto",
          outline: 0,
          padding: isMobile ? "15px" : "20px",
          position: "relative",
          maxHeight: "95vh",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column"
        }}
      >
        <CancelOutlinedIcon
          sx={{
            position: "absolute",
            right: 1,
            top: 1,
            color: "var(--priceText)",
            cursor: "pointer",
            transition: "all 80ms linear",
            ":hover": {
              color: "var(--notRed)",
              transition: "all 80ms linear",
            },
          }}
          onClick={handleShipmentDetailClose}
        />
        <h6
          style={{
            color: "var(--priceText)",
            display: "flex",
            alignItems: "center",
            gap: "5px",
            marginBottom: "20px",
          }}
        >
          <PlagiarismOutlinedIcon />
          Gönderi Takip
        </h6>

        {/* Loading State */}
        {isLoading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "300px",
              flexDirection: "column",
              gap: 2
            }}
          >
            <CircularProgress size={40} />
            <div style={{ color: "var(--priceText)", fontSize: "14px" }}>
              Gönderi bilgileri yükleniyor...
            </div>
          </Box>
        )}

        {/* Error State */}
        {hasError && (
          <Box sx={{ marginBottom: "20px" }}>
            <Alert severity="error" sx={{ borderRadius: "var(--inputBorderRadius)" }}>
              {errorMessage}
            </Alert>
          </Box>
        )}

        {/* Main Content - sadece loading ve error yoksa göster */}
        {!isLoading && !hasError && (
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: "var(--gap)",
              flex: 1,
              minHeight: 0,
              overflow: "hidden"
            }}
          >
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "flex-start",
                padding: isMobile ? "5px" : "10px",
                minWidth: 0,
                flex: isMobile ? "none" : "0 0 50%",
                order: isMobile ? 2 : 1
              }}
            >
              <div
                style={{
                  minHeight: isMobile ? "150px" : "200px",
                  maxWidth: isMobile ? "100%" : "392px",
                  width: "100%",
                  borderRadius: "var(--inputBorderRadius)",
                  padding: isMobile ? "15px" : "20px",
                  overflowY: "auto",
                  maxHeight: isMobile ? "300px" : "100%",
                  backgroundColor: "rgba(0, 0, 0, 0.02)"
                }}
              >
                <div style={{ color: "var(--shipmentLight)", fontSize: "14px" }}>
                  Gönderi Numarası
                </div>
                <div
                  style={{
                    color: "var(--priceText)",
                    fontWeight: 700,
                    fontSize: "18px",
                    margin: "5px 0",
                  }}
                >
                  {shipmentDetailModal.shipment?.parcel_ref_no || "Gönderi numarası bulunamadı"}
                </div>
                <br />
                <div style={{ fontSize: "14px", marginBottom: "5px" }}>
                  İzleme No; (Gönderinizi takip etmek için lütfen aşağıdaki takip numarasına
                  tıklayınız.)
                </div>
                <Button
                  className="iyzi-button-blue"
                  onClick={() =>
                    getTrackingLinkByCargo(
                      trackingCargo,
                      shipmentDetailModal.shipment?.tracking_awb === null
                        ? "İzleme numarası bulunmamaktadır."
                        : shipmentDetailModal.shipment?.tracking_awb
                    )
                  }
                >
                  {shipmentDetailModal.shipment?.tracking_awb === null
                    ? "İzleme numarası bulunmamaktadır."
                    : shipmentDetailModal.shipment?.tracking_awb}
                </Button>
                <Divider
                  sx={{
                    height: "1px",
                    backgroundColor: "var(--textPrice)",
                    width: "100%",
                    margin: "var(--gap) 0",
                  }}
                />
                {shipmentDetailModal.data && shipmentDetailModal.data.length > 0 ? (
                  shipmentDetailModal.data.map((item, key) => (
                    <div key={key}>
                      {key === 0 ? (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "var(--gap)",
                            marginBottom: "var(--gap)",
                          }}
                        >
                          <div
                            id="current-shipment-status"
                            style={{
                              backgroundColor: "#E8F9EE",
                              width: "38px",
                              height: "38px",
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              minWidth: "38px",
                              maxWidth: "38px",
                            }}
                          >
                            <div
                              style={{
                                backgroundColor: "#0EBC93",
                                width: "14px",
                                height: "14px",
                                borderRadius: "50%",
                              }}
                            />
                          </div>
                          <p>
                            {MessageTime(item.date)} - {item.description}
                          </p>
                        </div>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "var(--gap)",
                            marginBottom: "var(--gap)",
                          }}
                        >
                          <div
                            id="before-shipment-status"
                            style={{
                              background: "rgba(133, 185, 237, 0.2)",
                              width: "38px",
                              height: "38px",
                              minWidth: "38px",
                              maxWidth: "38px",
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <FmdGoodIcon sx={{ color: "var(--info)" }} />
                          </div>
                          <p>
                            {MessageTime(item.date)} - {item.description}
                          </p>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div style={{
                    textAlign: "center",
                    color: "var(--shipmentLight)",
                    fontSize: "14px",
                    padding: "20px 0"
                  }}>
                    Takip bilgisi bulunamadı
                  </div>
                )}
              </div>
            </div>
            {/* Divider kaldırıldı */}
            <div style={{
              width: "100%",
              padding: isMobile ? "5px" : "10px",
              minWidth: 0,
              flex: isMobile ? "none" : "0 0 50%",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              order: isMobile ? 1 : 2,
              gap: "10px"
            }}>
              {/* Harita Başlığı */}
              <div style={{
                width: "100%",
                textAlign: "center",
                color: "var(--priceText)",
                fontSize: "16px",
                fontWeight: "600",
                marginBottom: "5px"
              }}>
                Gönderi Rota Haritası
              </div>

              {/* Rota Bilgileri */}
              {senderCountryCoordinates?.coordinate && receiverCountryCoordinates?.coordinate && (
                <div style={{
                  display: "flex",
                  justifyContent: "flex-start",
                  gap: "20px",
                  width: "100%",
                  padding: "8px 12px",
                  backgroundColor: "rgba(74, 144, 226, 0.1)",
                  borderRadius: "8px",
                  fontSize: "12px",
                  color: "var(--priceText)"
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <div style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      backgroundColor: "#e74c3c"
                    }}></div>
                    <span>Başlangıç</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <div style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      backgroundColor: "#27ae60"
                    }}></div>
                    <span>Varış</span>
                  </div>
                </div>
              )}
              <div style={{
                width: "100%",
                height: isMobile ? "220px" : isTablet ? "300px" : "380px",
                maxHeight: "100%",
                overflow: "hidden",
                borderRadius: "var(--inputBorderRadius)",
                position: "relative",
                alignSelf: "flex-start"
              }}>
                {/* CSS Animasyonları */}
                <style>
                  {`
                  @keyframes dash {
                    to {
                      stroke-dashoffset: -10;
                    }
                  }
                  
                  @keyframes pulse {
                    0% {
                      transform: scale(1);
                      opacity: 1;
                    }
                    50% {
                      transform: scale(1.2);
                      opacity: 0.7;
                    }
                    100% {
                      transform: scale(1);
                      opacity: 1;
                    }
                  }
                  
                  .map-container {
                    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
                    backdrop-filter: blur(10px);
                  }
                `}
                </style>
                <ComposableMap
                  projection="geoNaturalEarth1"
                  projectionConfig={{
                    scale: isMobile ? 130 : isTablet ? 170 : 220,
                    center: [0, 10],
                  }}
                  className="map-container"
                  style={{
                    width: "100%",
                    height: "100%",
                    background: "transparent"
                  }}
                >
                  {/* SVG Tanımları */}
                  <defs>
                    <filter id="glow">
                      <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                      <feMerge>
                        <feMergeNode in="coloredBlur" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  </defs>

                  {/* Okyanus arka planı - kaldırıldı */}

                  {/* Grid çizgileri - daha subtle */}
                  <Graticule stroke="rgba(0,0,0,0.1)" strokeWidth={0.3} />

                  {/* Ülkeler - gerçekçi renkler */}
                  <Geographies geography={features}>
                    {({ geographies }) =>
                      geographies.map((geo) => (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          fill="#8fbc8f"
                          stroke="#5f8a5f"
                          strokeWidth={0.3}
                          style={{
                            default: {
                              fill: "#8fbc8f",
                              stroke: "#5f8a5f",
                              strokeWidth: 0.3,
                              outline: "none",
                            },
                            hover: {
                              fill: "#9acd32",
                              stroke: "#6b8e23",
                              strokeWidth: 0.5,
                              outline: "none",
                            },
                            pressed: {
                              fill: "#9acd32",
                              stroke: "#6b8e23",
                              strokeWidth: 0.5,
                              outline: "none",
                            },
                          }}
                        />
                      ))
                    }
                  </Geographies>

                  {/* Rota çizgisi - daha güzel ve animasyonlu */}
                  {senderCountryCoordinates?.coordinate && receiverCountryCoordinates?.coordinate && (
                    <>
                      {/* Ana rota çizgisi */}
                      <Line
                        from={[
                          Number(senderCountryCoordinates.coordinate[1]),
                          Number(senderCountryCoordinates.coordinate[0]),
                        ]}
                        to={[
                          Number(receiverCountryCoordinates.coordinate[1]),
                          Number(receiverCountryCoordinates.coordinate[0]),
                        ]}
                        stroke="#e74c3c"
                        strokeWidth={3}
                        strokeLinecap="round"
                        filter="url(#glow)"
                        strokeDasharray="8,4"
                        style={{
                          animation: "dash 3s linear infinite"
                        }}
                      />
                    </>
                  )}

                  {/* Başlangıç noktası marker */}
                  {senderCountryCoordinates?.coordinate && (
                    <Marker
                      coordinates={[
                        Number(senderCountryCoordinates.coordinate[1]),
                        Number(senderCountryCoordinates.coordinate[0])
                      ]}
                    >
                      <circle
                        r={6}
                        fill="#e74c3c"
                        stroke="#fff"
                        strokeWidth={2}
                        style={{
                          animation: "pulse 3s infinite"
                        }}
                      />
                      <text
                        textAnchor="middle"
                        y={-12}
                        style={{
                          fontFamily: "system-ui",
                          fill: "#2c3e50",
                          fontSize: "10px",
                          fontWeight: "600"
                        }}
                      >
                        Başlangıç
                      </text>
                    </Marker>
                  )}

                  {/* Bitiş noktası marker */}
                  {receiverCountryCoordinates?.coordinate && (
                    <Marker
                      coordinates={[
                        Number(receiverCountryCoordinates.coordinate[1]),
                        Number(receiverCountryCoordinates.coordinate[0])
                      ]}
                    >
                      <circle
                        r={6}
                        fill="#27ae60"
                        stroke="#fff"
                        strokeWidth={2}
                        style={{
                          animation: "pulse 3s infinite"
                        }}
                      />
                      <text
                        textAnchor="middle"
                        y={-12}
                        style={{
                          fontFamily: "system-ui",
                          fill: "#2c3e50",
                          fontSize: "10px",
                          fontWeight: "600"
                        }}
                      >
                        Varış
                      </text>
                    </Marker>
                  )}
                </ComposableMap>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ShipmentDetail;
